/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { CMSButton } from '../../CMSButton/CMSButton';
import type { ContentStackProductGrid } from '../../../interface/content-types';
import styles from './CMSProductCard.module.scss';
import cx from 'classnames';
import WarrantyBadge from '@/components/AZCustomComponent/WarrantyBadge';
import { CMSLink } from '../../CMSLink/CMSLink';
import { useLabels } from '@/hooks/useLabels';
import { CMSImage } from '../../CMSImage/CMSImage';
import { type ViewProps } from '@az/starc-ui';

type CSProduct = Exclude<ContentStackProductGrid['product_card'], undefined>[0];
type Props = {
  product: CSProduct;
  detailsAlignment: ContentStackProductGrid['product_details_alignment'];
  isHorizontalScrollEnabled?: boolean;
};

const warrantyText: Record<Exclude<CSProduct['warranty'], undefined>, string> = {
  One: '1',
  Two: '2',
  Three: '3',
  Four: '4',
  Five: '5',
};

const classNameForDetailAlignment: Record<
  Exclude<ContentStackProductGrid['product_details_alignment'], undefined>,
  string
> = {
  Left: styles.contentLeftAligned,
  Center: styles.contentCenterAligned,
};
const labelMap = {
  yearWarrantyLabel: 'label_MyWarrantiesDetails_YearWarranty',
};

export const CMSProductCard = (props: Props) => {
  const {
    product: { image, product_name, link, product_description, warranty, button },
    detailsAlignment = 'Center',
    isHorizontalScrollEnabled,
  } = props;
  const labels = useLabels(labelMap);
  const buttonAlignment: ViewProps<'div'>['justify'] =
    detailsAlignment === 'Center' ? 'center' : 'start';
  const maybeLinkChildren = (
    <>
      {image && (
        <CMSImage
          image={image}
          className={cx(styles.image, { [styles.mobileScrollImage]: isHorizontalScrollEnabled })}
        />
      )}
      <div className={cx(styles.title, { [styles.mobileScrollTitle]: isHorizontalScrollEnabled })}>
        {product_name}
      </div>
    </>
  );

  return (
    <div
      className={cx(styles.productCardContent, classNameForDetailAlignment[detailsAlignment], {
        [styles.mobileScrollContent]: isHorizontalScrollEnabled,
      })}
    >
      <div className={styles.productCardLink}>
        {link ? <CMSLink to={link}>{maybeLinkChildren}</CMSLink> : maybeLinkChildren}
        {product_description && <p data-testid="product-card-description">{product_description}</p>}
        {warranty ? (
          <WarrantyBadge
            warranty={`${warrantyText[warranty]}${labels.yearWarrantyLabel}`}
            classes={{ root: styles.warranty }}
          />
        ) : null}
      </div>
      {button?.theme ? <CMSButton options={button} justify={buttonAlignment} /> : null}
    </div>
  );
};
